import { IShortcut } from '@/components/SectionShortcuts';
import { IShippingAddress } from '@/types';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';

export const ALLOWED_FILE_TYPES = [
  'text/plain',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
  'text/xml',
  'text/csv',
];

export const ALLOWED_DOCUMENT_FILE_TYPES = [
  'application/pdf',
  'image/jpg',
  'image/jpeg',
  'image/png',
];

export const ALLOWED_IMAGE_FILE_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/png',
];

export const MAXIMUM_IMG_SIZE_ALLOWED = 30 * 1024 * 1024; //30mb
export const MAXIMUM_IMG_SIZE_ALLOWED_PORTFOLIO = 6 * 1024 * 1024; //6mb
export const MAX_IMAGES_PORTFOLIO = 5;

export const SHORTCUT: Array<IShortcut> = [
  {
    name: 'Minha Página',
    href: '/',
    icon: StorefrontIcon,
    id: 1,
  },
  {
    name: 'Perfil',
    href: '/perfil',
    icon: PersonOutlineIcon,
    id: 2,
  },
  {
    name: 'Cotações',
    href: '/cotacoes',
    icon: AssignmentIcon,
    id: 3,
  },
  {
    name: 'Prestadores',
    href: '/prestadores',
    icon: BuildIcon,
    id: 4,
  },
  {
    name: 'Ordens de compra',
    href: '/ordens-de-compra',
    icon: DescriptionIcon,
    id: 5,
  },
  {
    name: 'Painel de controle',
    href: '/painel-de-controle',
    icon: DashboardIcon,
    id: 6,
  },
];

export const URL_MAPS_SHIPPING_ADDRESS = (address: IShippingAddress) => {
  return `${URL_GOOGLE_MAPS}/search/${address.street} ${address.number} ${address.neighbourhood} ${address.city}`;
};

export const URL_GOOGLE_MAPS = 'https://www.google.com/maps';

export const ROUTER_PAGES = {
  obra_play: 'https://www.obraplay.com/',
  resend_activation: '/reenviar-ativacao/',
  lp_supplier: 'https://www.obraplay.com/fornecedor',
  index: '/',
  home: '/home',
  login(next?: string) {
    return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
  },
  cadastro: '/cadastro',
  ordensDeCompra: '/ordens-de-compra',
  esqueciMinhaSenha(next?: string) {
    return next
      ? `/esqueci-minha-senha?next=${encodeURIComponent(next)}`
      : '/esqueci-minha-senha';
  },
  notificacoes: '/notificacoes',
  usuarios: '/usuarios',
  privacidade: '/privacidade',
  termosUso: '/termos-de-uso',
  primeiroAcesso: '/primeiro-acesso',
  resetarSenha: '/resetar-senha',
  ajuda: 'https://ajuda.obraplay.com.br',
  respostaCotacoes: {
    index: '/resposta-cotacoes',
    index_chave: (
      id: string | string[] | undefined,
      chave: string | string[] | undefined
    ) => `/resposta-cotacoes/${id}?chave=${chave}`,
    index_chave_utm: (
      quotationAnswerId: number,
      chave: string | string[] | undefined,
      utmMedium: string,
      utmSource: string
    ) =>
      `/resposta-cotacoes/${quotationAnswerId}?chave=${chave}&utm_medium=${utmMedium}&utm_source=${utmSource}`,
    pagamento: (id: string | string[] | undefined) => {
      return `/resposta-cotacoes/${id}/pagamento`;
    },
    pagamento_chave: (
      id: string | string[] | undefined,
      chave: string | string[] | undefined
    ) => {
      return `/resposta-cotacoes/${id}/pagamento?chave=${chave}`;
    },
    resumo: (
      quotationAnswerId: number,
      chave?: string | string[] | undefined
    ) => {
      return `/resposta-cotacoes/${quotationAnswerId}/resumo?chave=${chave}`;
    },
  },
  site: '/site',
  vitrinePreco: '/vitrine-preco',
  400: '/400',
  500: '/500',
} as const;

export const KEY_REGEX =
  '[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}';

export const ALLOWED_NEXT_PAGES_REGEX = {
  quotationAnswerResume: new RegExp(
    `\\/resposta-cotacoes\\/\\d+\\/resumo\\?chave=${KEY_REGEX}`
  ),
  order: new RegExp(`\\/ordens-de-compra\\/\\d+\\?chave=${KEY_REGEX}`),
  login: /\/login(?:\?[\w]+=true)?\/?/,
  home: /\/home(?:\?[\w]+=true)\/?/,
};

export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

export const CURRENT_ENVIRONMENT_URL = {
  develop: 'https://app-dev.obraplay.com',
  staging: 'https://app-staging.obraplay.com',
  production: 'https://app.obraplay.com',
}[process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT];

export const PAYMENT_METHODS = [
  {
    id: 11,
    code: 'bankslip',
    name: 'Boleto',
  },
  {
    id: 10,
    code: 'other',
    name: 'Outro',
  },
  {
    id: 9,
    code: 'transfer',
    name: 'Transferência',
  },
  {
    id: 7,
    code: 'pix',
    name: 'PIX',
  },
  {
    id: 5,
    code: 'money',
    name: 'Dinheiro',
  },
  {
    id: 4,
    code: 'bankdeposit',
    name: 'Depósito Bancário',
  },
  {
    id: 3,
    code: 'automaticdebit',
    name: 'Débito Automático',
  },
  {
    id: 2,
    code: 'check',
    name: 'Cheque',
  },
  {
    id: 1,
    code: 'creditcard',
    name: 'Cartão de Crédito',
  },
  {
    id: 21,
    code: 'debitcard',
    name: 'Cartão de Débito',
  },
  {
    id: 8,
    code: 'ted',
    name: 'TED',
  },
  {
    id: 6,
    code: 'doc',
    name: 'DOC',
  },
];

export const INSTALLMENTS_POSSIBILITIES = [
  { value: 1, name: '1x' },
  { value: 2, name: '2x' },
  { value: 3, name: '3x' },
  { value: 4, name: '4x' },
  { value: 5, name: '5x' },
  { value: 6, name: '6x' },
  { value: 7, name: '7x' },
  { value: 8, name: '8x' },
  { value: 9, name: '9x' },
  { value: 10, name: '10x' },
  { value: 11, name: '11x' },
  { value: 12, name: '12x' },
];

export type SelectedType =
  | 'all'
  | 'has_fast_purchase_enabled'
  | 'is_updated'
  | 'is_expired'
  | 'is_pending'
  | 'not_available';

export const numericCharacters = '123456789';

export const charactersToTest = '!@#$%¨&*()_|+=[]~;:/?{}ºª"\'.,°§¬¢£³²¹\\';

export const URL_DOMAIN_REGEX = /^(https?:\/\/[^/]+\/)/;

export const ORDER_REFUSAL_REASONS = {
  valueNotAgreed: 'Valor não está de acordo',
  doNotHaveAĺlItems: 'Não possuo todos os itens',
  missingDetailsDescription: 'Descrição do item falta detalhes ou está errada',
  registrationWithPendingIssues: 'Cadastro da empresa possui pendências',
  divergentMaterial: 'Material divergente',
  minimumQuantityNotReached: 'Não atinge a quantidade mínima de pedido',
  canNotMeetDeadline: 'Não consigo atender no prazo solicitado',
  notDeliverInAddress: 'Não fazemos entregas no endereço informado',
  notInterestingInSelling: 'Não tenho interesse nessa venda',
  customerRequestCancel: 'Cliente solicitou o cancelamento',
} as const;

export const QUOTATION_REFUSAL_REASONS = {
  dontProvideRequestedItem: 'Não fornecemos o item solicitado',
  doNotHaveItemsInInventory: 'Não possuo o produto em estoque',
  notDeliverInAddress: 'Não fazemos entregas no endereço informado',
  minimumQuantityNotReached: 'Não atinge a quantidade mínima de pedido',
  notWorkWithBrand: 'Não trabalho com a marca solicitada',
  canNotMeetDeadline: 'Não consigo atender no prazo solicitado',
  missingDetailsDescription: 'Descrição do item falta detalhes ou está errada',
  notInterestingInSelling: 'Não tenho interesse nessa venda',
  difficultToUse: 'Dificuldade de uso',
} as const;

export const SUPPORT_WHATSAPP = '5541996306909';
